import { makeAutoObservable } from 'mobx';

import { DesignerStore } from 'controls/designer/designerStore';
import { MxCell } from 'controls/designer/mxGraphInterfaces';
import { MobxManager } from 'framework/mobx-integration';
import {
	checkCellForWidget,
	destroyWidget,
	destroyWidgetById,
	iterateWidgets,
} from 'controls/designer/features/widgets/tools';
import { CustomDataEntry } from 'controls/designer/customDataEntry';
import { updateNullFields } from 'tools/helpers/array';
import { WidgetConfig } from 'controls/designer/features/widgets/widgetConfig';
import {
	defaultRedirectConfig,
	RedirectConfig,
} from 'controls/designer/features/redirectOnClick/redirectConfig';
import { getWidgetDescription } from 'controls/designer/features/widgets/utils/getWidgetDescription';

type CreateWidgetArgs = {
	config: WidgetConfig;
	cell?: MxCell;
	redirectConfig?: RedirectConfig;
	resetSize?: true;
	x?: number;
	y?: number;
};

export class WidgetsManager {
	mobx = new MobxManager();
	constructor(public store: DesignerStore) {
		makeAutoObservable(this);
	}

	init() {}

	get(cell: MxCell) {
		return this.store.getCustomData(cell)?.widgetConfig;
	}

	getEffectiveConfig(cell: MxCell) {
		let config = this.get(cell);

		if (!config) return null;

		const widgetDescription = getWidgetDescription(config.type);
		if (widgetDescription == null) return;

		updateNullFields(config, widgetDescription.defaultConfig);
		if (config.configuration && widgetDescription.defaultConfig.configuration) {
			updateNullFields(config.configuration, widgetDescription.defaultConfig.configuration);
		}

		if (widgetDescription.preProcessConfig) {
			config = widgetDescription.preProcessConfig(config, this.store);
		}

		return config;
	}

	onCustomDataAdded = (customDataList: CustomDataEntry[]) => {
		customDataList.forEach((x) => {
			if (x.widgetConfig) {
				this.checkCellForWidget(x.cell);
			}
		});
	};

	onCustomDataRemoved = (customData: CustomDataEntry) => {
		this.destroyWidget(customData.cell);
	};

	destroyWidget(cell: MxCell) {
		destroyWidget(this.store.legacyDesigner, cell);
	}

	checkCellForWidget(cell: MxCell) {
		checkCellForWidget(this.store.graph, cell);
	}

	createOrUpdateWidget(args: CreateWidgetArgs) {
		let { config, redirectConfig, cell } = args;

		const designer = this.store.legacyDesigner;

		const widgetDescription = getWidgetDescription(config.type);

		const containerStyle =
			typeof widgetDescription.containerStyle == 'function'
				? widgetDescription.containerStyle(config)
				: widgetDescription.containerStyle;

		const currentStyle = cell ? designer.graph.getCellStyle(cell) : null;

		const strokeColor =
			containerStyle?.noBorder == true
				? 'none'
				: currentStyle
					? (currentStyle.strokeColor ?? 'none')
					: '#E6E6E6';

		const fillColor = containerStyle?.transparent
			? 'none'
			: currentStyle
				? (currentStyle.fillColor ?? 'none')
				: '#FFFFFF';

		const widgetHeader = containerStyle?.header ?? 'default';

		const width =
			widgetDescription.width ??
			Math.max(widgetDescription.minWidth ?? 0, designer.dashboardSettings.widgetWidth);
		const height = widgetDescription.height ?? designer.dashboardSettings.widgetHeight;

		if (!cell) {
			cell = this.store.graphManager.createHtmlShape({
				x: args?.x ?? 50,
				y: args?.y ?? 50,
				width: width,
				height: height,
				style: `strokeColor=${strokeColor};fillColor=${fillColor};widgetHeader=${widgetHeader}`,
			});
		} else {
			this.store.graphManager.executeUpdate(() => {
				if (args.resetSize) {
					designer.graph
						.getModel()
						.setGeometry(cell, cell.getGeometry().copy(null, null, width, height));
				}
				designer.graph.setCellStyles('strokeColor', strokeColor, [cell]);
				designer.graph.setCellStyles('fillColor', fillColor, [cell]);
				designer.graph.setCellStyles('widgetHeader', widgetHeader, [cell]);
			});
		}

		const customData = this.store.getOrCreateCustomData(cell);
		customData.widgetConfig = config;
		customData.redirectConfig = redirectConfig ?? { ...defaultRedirectConfig };

		destroyWidget(designer, cell);
		checkCellForWidget(designer.graph, cell);

		return cell;
	}

	destroy() {
		this.mobx.destroy();

		iterateWidgets(this.store.graph, (widgetEntry: WidgetsMapEntry) => {
			destroyWidgetById(this.store.graph, widgetEntry.id);
		});
	}
}

export type WidgetsMapEntry = {
	id: string;
};
